import { Link } from 'react-router-dom';

/** Placeholder Privacy Policy. Same disclaimer as Terms: this needs to
 * be finalized by counsel and reviewed against GDPR / CCPA before the
 * self-serve signup flow opens to the public. */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-bg text-ink">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-xs text-dim mb-6">
          <Link to="/" className="hover:text-ink transition">← Home</Link>
        </div>
        <h1 className="text-2xl font-medium mb-2">Privacy Policy</h1>
        <div className="text-xs text-dim mb-8">
          Effective: {'{{EFFECTIVE_DATE}}'}
        </div>

        <div className="text-sm text-warn/90 border border-warn/30 rounded p-3 mb-8">
          <strong>Placeholder.</strong> This page is a stub until Sendepot's
          Privacy Policy is finalized. It's here to signal the shape of
          our data practices, not as a legally binding document.
        </div>

        <Section title="1. What we collect">
          <ul className="list-disc list-outside pl-5 space-y-1">
            <li>
              <strong>Account:</strong> your email, hashed password, and
              workspace name.
            </li>
            <li>
              <strong>Microsoft tenant credentials:</strong> the admin
              username and password you supply so we can provision.
              Stored encrypted at rest; used only for automation.
            </li>
            <li>
              <strong>Mailbox metadata:</strong> the local-part, display
              name, and current OAuth status of each mailbox we
              provision on your behalf. We do NOT read the content of
              the emails those mailboxes send or receive.
            </li>
            <li>
              <strong>Sequencer API keys:</strong> encrypted at rest;
              used only to upload mailbox credentials to the sequencer
              account you own.
            </li>
            <li>
              <strong>Operational logs:</strong> DNS and provisioning
              events, retained for {'{{LOG_RETENTION}}'} to support
              debugging.
            </li>
          </ul>
        </Section>

        <Section title="2. How we use it">
          Strictly to operate the service you signed up for. We do not
          sell your data. We do not use tenant credentials outside the
          automation pipeline. Aggregate, non-personal metrics
          (provisioning volume, success rate) may inform product
          improvements.
        </Section>

        <Section title="3. Sub-processors">
          Sendepot uses the following sub-processors:
          <ul className="list-disc list-outside pl-5 mt-2 space-y-1">
            <li>Railway / {'{{CLOUD_HOST}}'} — application + database hosting</li>
            <li>Cloudflare — DNS management for your sending domains</li>
            <li>Microsoft — provisioning your Microsoft 365 tenants (as you direct)</li>
            <li>The sequencer you choose (Instantly, Smartlead, etc.) — you configure this directly</li>
            <li>Resend — transactional email (magic links, verification)</li>
          </ul>
        </Section>

        <Section title="4. Your rights">
          You may request an export or deletion of your workspace data
          at any time via <a href="mailto:support@sendepot.com" className="text-accent hover:underline">support@sendepot.com</a>.
          Self-serve export is available at Settings → Data export.
        </Section>

        <Section title="5. Data location & security">
          Data is stored in {'{{PRIMARY_REGION}}'}. Encryption in
          transit (TLS) is enforced on all endpoints. Application-level
          secrets (tenant credentials, sequencer keys) are encrypted at
          rest with keys managed via {'{{KMS_PROVIDER}}'}.
        </Section>

        <Section title="6. Contact">
          Data protection / privacy questions:
          <a href="mailto:support@sendepot.com" className="text-accent hover:underline"> support@sendepot.com</a>.
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="text-sm text-ink font-medium mb-2">{title}</h2>
      <div className="text-sm text-dim leading-relaxed">{children}</div>
    </section>
  );
}
